import { useTranslation } from "react-i18next";

import { ISite } from "../../models/Site";
import classNames from "../../utils/classNames";

type SiteScopeSelectProps = {
  value: string;
  onChange: (siteId: string) => void;
  sites: ISite[];
  size?: "sm" | "md" | "lg";
  className?: string;
  disabled?: boolean;
};

/**
 * Compact depot picker that sits in front of a location combobox and narrows its options.
 * Renders nothing when there are no sites, so single-depot yards never see it.
 */
export default function SiteScopeSelect({ value, onChange, sites, size = "md", className = "", disabled = false }: SiteScopeSelectProps) {
  const { t } = useTranslation("common");

  if (sites.length === 0) return null;

  const sizeClasses = {
    sm: "h-8 py-0 pl-2 pr-7 text-xs",
    md: "h-10 py-0 pl-2 pr-8 text-sm",
    lg: "h-12 py-0 pl-3 pr-8 text-base",
  };

  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      title={t("Site")}
      aria-label={t("Site")}
      className={classNames(
        "shrink-0 rounded-md border-gray-300 bg-gray-50 text-gray-700 focus:border-primary-500 focus:ring-primary-500 disabled:opacity-50",
        sizeClasses[size],
        className
      )}
    >
      <option value="">{t("Any site")}</option>
      {sites.map((site) => (
        <option key={site.id} value={site.id}>{site.shortCode || site.name}</option>
      ))}
    </select>
  );
}
